import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { VegaEmbed } from 'react-vega'
import type { VisualizationSpec } from 'vega-embed'
import type { Config } from 'vega-lite'

import { nextSortDirection, SortableTableHeader } from '../components/SortableTableHeader'
import { Api, ApiError } from '../services/api'

const ROI_TABLE_CAP = 2500

const VEGA_CONFIG: Config = {
  axis: {
    labelColor: '#64748b',
    titleColor: '#64748b',
    gridColor: '#e2e8f0',
    domainColor: '#cbd5e1',
    labelFontSize: 11,
    titleFontSize: 12,
  },
  legend: { labelColor: '#334155', titleColor: '#334155', labelFontSize: 12 },
  view: { stroke: null },
}

/** Header order matching the shared Google Sheet (screenshot). */
const SHEET_COLUMN_SPEC: readonly { label: string; match: (raw: string) => boolean }[] = [
  { label: 'Date', match: (a) => /^date$/i.test(a.trim()) },
  { label: 'Sport', match: (a) => /^sport$/i.test(a.trim()) },
  { label: 'Site', match: (a) => /^site$/i.test(a.trim()) },
  { label: 'Tour', match: (a) => /^tour$/i.test(a.trim()) },
  { label: 'Event', match: (a) => /^event$/i.test(a.trim()) },
  { label: 'Golfer', match: (a) => /^golfer$/i.test(a.trim()) },
  { label: 'Type', match: (a) => /^type$/i.test(a.trim()) },
  { label: '$', match: (a) => a.trim() === '$' },
  { label: 'Odds', match: (a) => /^odds$/i.test(a.trim()) },
  { label: 'Win', match: (a) => /^win$/i.test(a.trim()) },
  { label: 'Result', match: (a) => /^result$/i.test(a.trim()) },
]

type SheetColumn = { id: string; label: string }

function resolveSheetColumns(available: string[]): SheetColumn[] {
  const used = new Set<string>()
  const out: SheetColumn[] = []
  for (const { label, match } of SHEET_COLUMN_SPEC) {
    const found = available.find((a) => !used.has(a) && match(a))
    if (found) {
      out.push({ id: found, label })
      used.add(found)
    }
  }
  return out
}

/** Subset of columns for API `fields=` — sheet grid order + ROI drivers; cuts JSON vs SELECT *. */
function bettingHistoryFieldsCsv(columnNames: string[]): string | null {
  const visible = resolveSheetColumns(columnNames)
  const ordered: string[] = visible.map(({ id }) => id)
  const d = findDateColumn(columnNames)
  const s = findStakeColumn(columnNames)
  const r = findResultColumn(columnNames)
  for (const x of [d, s, r]) {
    if (x && !ordered.includes(x)) ordered.push(x)
  }
  if (columnNames.includes('_sheet') && !ordered.includes('_sheet')) ordered.push('_sheet')
  if (ordered.length === 0) return null
  return ordered.join(',')
}

function parseNumberLoose(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string') {
    const t = v.replace(/,/g, '').trim()
    if (!t) return null
    const n = Number(t)
    return Number.isFinite(n) ? n : null
  }
  return null
}

/** Parse sheet-style dates (M/D/YYYY, ISO) to YYYY-MM-DD for Vega temporal axis. */
function parseBetDateKey(v: unknown): string | null {
  if (v == null) return null
  if (typeof v === 'number' && Number.isFinite(v)) {
    const epoch = Date.UTC(1899, 11, 30)
    const ms = epoch + v * 86400000
    if (!Number.isFinite(ms)) return null
    return formatDayUTC(new Date(ms))
  }
  if (typeof v !== 'string') return null
  const s = v.trim()
  if (!s) return null
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`
  const us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/) ?? s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/)
  if (us) {
    let y = Number(us[3])
    if (y < 100) y += 2000
    const m = Number(us[1])
    const d = Number(us[2])
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31 && y >= 1900 && y < 2100)
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
  }
  const t = Date.parse(s)
  if (!Number.isNaN(t)) return formatDayUTC(new Date(t))
  return null
}

function formatDayUTC(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function findDateColumn(columns: string[]): string | null {
  const byName = columns.find((c) => /^(date|bet_date)$/i.test(c.trim()))
  if (byName) return byName
  return columns.find((c) => /date|week|day/i.test(c)) ?? null
}

function findStakeColumn(columns: string[]): string | null {
  if (columns.includes('$')) return '$'
  return (
    columns.find((c) => {
      const n = c.trim().toLowerCase().replace(/\s+/g, '_')
      return ['stake', 'wager', 'amount', 'bet', 'risk', 'units'].includes(n)
    }) ?? null
  )
}

function findResultColumn(columns: string[]): string | null {
  return (
    columns.find((c) => {
      const n = c.trim().toLowerCase()
      return n === 'result' || n === 'profit' || n === 'pnl' || n === 'net' || n === 'net_units'
    }) ?? null
  )
}

const GOLF_FILTER_YEARS = [2024, 2025, 2026] as const

type ChartMetric = 'roi' | 'profit' | 'wagered'

function totalsFromValidBetRows(
  rows: Record<string, unknown>[],
  dateCol: string,
  stakeCol: string,
  resultCol: string,
): { stakeSum: number; resultSum: number } {
  let stakeSum = 0
  let resultSum = 0
  for (const r of rows) {
    const dk = parseBetDateKey(r[dateCol])
    const stake = parseNumberLoose(r[stakeCol])
    const result = parseNumberLoose(r[resultCol])
    if (dk == null || stake == null || result == null || stake <= 0) continue
    stakeSum += stake
    resultSum += result
  }
  return { stakeSum, resultSum }
}

type RoiPoint = { date: string; roiPct: number; cumStakes: number; cumResult: number }

function buildCumulativeRoiSeries(
  rows: Record<string, unknown>[],
  dateCol: string,
  stakeCol: string,
  resultCol: string,
): { series: RoiPoint[]; usedRows: number; skippedRows: number } {
  type DayAgg = { stake: number; result: number }
  const byDay = new Map<string, DayAgg>()
  let skippedRows = 0

  for (const r of rows) {
    const dk = parseBetDateKey(r[dateCol])
    const stake = parseNumberLoose(r[stakeCol])
    const result = parseNumberLoose(r[resultCol])
    if (dk == null || stake == null || result == null || stake <= 0) {
      skippedRows++
      continue
    }
    const cur = byDay.get(dk) ?? { stake: 0, result: 0 }
    cur.stake += stake
    cur.result += result
    byDay.set(dk, cur)
  }

  const days = [...byDay.keys()].sort()
  let cumStakes = 0
  let cumResult = 0
  const series: RoiPoint[] = []
  for (const date of days) {
    const { stake, result } = byDay.get(date)!
    cumStakes += stake
    cumResult += result
    const roiPct = cumStakes > 0 ? (cumResult / cumStakes) * 100 : 0
    series.push({ date, roiPct, cumStakes, cumResult })
  }

  return { series, usedRows: rows.length - skippedRows, skippedRows }
}

/** DuckDB double-quoted identifier + ASC/DESC for datasets `order_by` (handles `$`, spaces). */
function buildDuckOrderBySql(columnId: string, dir: 'asc' | 'desc'): string {
  const d = dir === 'desc' ? 'DESC' : 'ASC'
  const escaped = columnId.replace(/"/g, '""')
  return `"${escaped}" ${d}`
}

export function GolfBettingPage() {
  const [filterYear, setFilterYear] = useState<number | null>(null)
  const [chartMetric, setChartMetric] = useState<ChartMetric>('roi')

  const schemaQuery = useQuery({
    queryKey: ['betting_history', 'schema'],
    queryFn: async () => {
      try {
        return await Api.getDatasetSchema('betting_history')
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null
        throw e
      }
    },
  })

  const columnNames = useMemo(
    () => schemaQuery.data?.columns.map((c) => c.name) ?? [],
    [schemaQuery.data],
  )

  const fieldsCsv = useMemo(() => {
    if (!columnNames.length) return null
    return bettingHistoryFieldsCsv(columnNames)
  }, [columnNames])

  const dateColForApi = useMemo(() => findDateColumn(columnNames), [columnNames])

  const [filters, setFilters] = useState<Record<string, string>>({})
  const [sort, setSort] = useState<{ id: string; dir: 'asc' | 'desc' } | null>(null)

  const defaultSort = useMemo(() => {
    const vis = resolveSheetColumns(columnNames)
    const dateId = vis.find((c) => c.label === 'Date')?.id
    if (dateId) return { id: dateId, dir: 'asc' as const }
    const first = vis[0]?.id
    return first ? { id: first, dir: 'asc' as const } : null
  }, [columnNames])

  const sheetColKey = useMemo(
    () => resolveSheetColumns(columnNames).map((c) => c.id).join('|'),
    [columnNames],
  )

  const activeSort = sort ?? defaultSort

  const orderByForApi = useMemo(() => {
    if (!activeSort || !columnNames.includes(activeSort.id)) return undefined
    return buildDuckOrderBySql(activeSort.id, activeSort.dir)
  }, [activeSort, columnNames])

  const datasetQuery = useQuery({
    queryKey: ['betting_history', 'rows', fieldsCsv ?? 'all', filterYear ?? 'all', orderByForApi ?? ''],
    queryFn: async () => {
      return await Api.getDataset<Record<string, unknown>>('betting_history', {
        limit: 50_000,
        ...(fieldsCsv ? { fields: fieldsCsv } : {}),
        ...(filterYear != null && dateColForApi
          ? { year: filterYear, dateColumn: dateColForApi }
          : {}),
        ...(orderByForApi ? { orderBy: orderByForApi } : {}),
      })
    },
    enabled: schemaQuery.isSuccess && columnNames.length > 0 && !(filterYear != null && !dateColForApi),
  })

  const columns = datasetQuery.data?.columns ?? []
  const rows = datasetQuery.data?.rows ?? []

  const visibleSheetColumns = useMemo(() => resolveSheetColumns(columns), [columns])

  useEffect(() => {
    setFilters({})
  }, [columns.join('|'), filterYear])

  useEffect(() => {
    setSort(null)
  }, [sheetColKey])

  const columnMap = useMemo(() => {
    return {
      date: findDateColumn(columns),
      stake: findStakeColumn(columns),
      result: findResultColumn(columns),
    }
  }, [columns])

  const roiModel = useMemo(() => {
    const { date, stake, result } = columnMap
    if (!date || !stake || !result || !rows.length) {
      return { series: [] as RoiPoint[], usedRows: 0, skippedRows: 0, ready: false as const }
    }
    const { series, usedRows, skippedRows } = buildCumulativeRoiSeries(rows, date, stake, result)
    return { series, usedRows, skippedRows, ready: true as const }
  }, [columnMap, rows])

  const { tableRows, tableTotal } = useMemo(() => {
    let out = rows.filter((r) => {
      for (const { id } of visibleSheetColumns) {
        const q = (filters[id] ?? '').trim().toLowerCase()
        if (!q) continue
        const cell = fmtCell(r[id]).toLowerCase()
        if (!cell.includes(q)) return false
      }
      return true
    })
    const total = out.length
    return { tableRows: out.slice(0, ROI_TABLE_CAP), tableTotal: total }
  }, [rows, visibleSheetColumns, filters])

  const kpis = useMemo(() => {
    const { date, stake, result } = columnMap
    if (!date || !stake || !result) return null
    if (roiModel.series.length > 0) {
      const last = roiModel.series[roiModel.series.length - 1]!
      return {
        roiPct: last.roiPct,
        profit: last.cumResult,
        wagered: last.cumStakes,
      }
    }
    const t = totalsFromValidBetRows(rows, date, stake, result)
    return {
      roiPct: t.stakeSum > 0 ? (t.resultSum / t.stakeSum) * 100 : 0,
      profit: t.resultSum,
      wagered: t.stakeSum,
    }
  }, [columnMap, roiModel.series, rows])

  const chartSpec = useMemo((): VisualizationSpec => {
    const pts = roiModel.series
    const yEnc =
      chartMetric === 'roi'
        ? {
            field: 'roiPct' as const,
            title: 'Cumulative ROI (%)',
            axis: { format: '.1f' } as const,
            scale: { zero: false } as const,
            color: '#0d9488',
          }
        : chartMetric === 'profit'
          ? {
              field: 'cumResult' as const,
              title: 'Cumulative profit ($)',
              axis: { format: ',.0f' as const, labelOffset: 4 },
              scale: { zero: true } as const,
              color: '#2563eb',
            }
          : {
              field: 'cumStakes' as const,
              title: 'Cumulative wagered ($)',
              axis: { format: ',.0f' as const, labelOffset: 4 },
              scale: { zero: true } as const,
              color: '#7c3aed',
            }

    return {
      $schema: 'https://vega.github.io/schema/vega-lite/v6.json',
      description: 'Golf betting cumulative series',
      data: { values: pts },
      config: VEGA_CONFIG,
      width: 'container',
      height: 'container',
      // Default autosize ("pad") grows the SVG outside the specified plot box
      // to fit axes, so the rendered chart ended up taller than the parent
      // `h-[22rem]` and pushed the table below out of place. "fit" with
      // `contains: 'padding'` keeps the entire chart — axes included — inside
      // the container.
      autosize: { type: 'fit', contains: 'padding' },
      mark: { type: 'line', interpolate: 'monotone', strokeWidth: 2.5, color: yEnc.color, invalid: null },
      encoding: {
        x: { field: 'date', type: 'temporal', title: 'Bet date (by day)' },
        y: {
          field: yEnc.field,
          type: 'quantitative',
          title: yEnc.title,
          axis: yEnc.axis,
          scale: yEnc.scale,
        },
        tooltip: [
          { field: 'date', type: 'temporal', title: 'Date' },
          { field: 'roiPct', title: 'ROI %', format: '.2f' },
          { field: 'cumResult', title: 'Cumulative profit', format: '$,.0f' },
          { field: 'cumStakes', title: 'Cumulative wagered', format: '$,.0f' },
        ],
      },
    }
  }, [roiModel.series, chartMetric])

  const yearLabel = filterYear == null ? 'All years' : String(filterYear)
  const loading =
    schemaQuery.isPending ||
    (schemaQuery.isSuccess && columnNames.length > 0 && datasetQuery.isPending)
  const err =
    schemaQuery.isError ? schemaQuery.error : datasetQuery.isError ? datasetQuery.error : null
  const missing =
    schemaQuery.data === null && !schemaQuery.isPending && !schemaQuery.isError
  const colsOk = columnMap.date && columnMap.stake && columnMap.result

  function onHeaderClick(id: string) {
    setSort((prev) => {
      const base = prev ?? defaultSort
      if (base && base.id === id) return { id, dir: nextSortDirection(base.dir) }
      return { id, dir: 'asc' }
    })
  }

  function clearFilters() {
    setFilters({})
  }

  const filterActive = Object.values(filters).some((v) => v.trim())

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Golf bets</h1>
        <p className="text-sm text-slate-500">
          Betting history from the shared Google Sheet. Use <strong>Year</strong> to limit rows (chart, KPIs, and
          table) via the API. KPIs show <strong>ROI</strong>, <strong>cumulative profit</strong>, and{' '}
          <strong>total wagered</strong> for the selected period; the chart switches metric with the dropdown. Cumulative
          series reset within each filtered period (chronological bets in that year). Column filters apply on top of the
          year filter.
        </p>
      </div>

      {filterYear != null && !dateColForApi && schemaQuery.isSuccess && (
        <p className="text-sm text-amber-800">
          Year filter needs a parsable <strong>Date</strong> column in this dataset. Choose &quot;All years&quot; or
          ingest data with dates.
        </p>
      )}

      {missing && (
        <p className="text-sm text-amber-800">
          No <code className="text-xs">golf/betting_history.parquet</code> yet. Configure the sheet id and
          Google credentials on the pipeline worker, run <code className="text-xs">inputs</code> (or full{' '}
          <code className="text-xs">run_all.py</code>), then refresh.
        </p>
      )}

      {loading && (
        <p className="text-slate-500">
          Loading…{' '}
          <span className="text-xs text-slate-400">
            (fetching schema, then betting rows — ensure <code className="text-xs">./sm api dev</code> is running
            on port 8765)
          </span>
        </p>
      )}
      {err && <p className="text-rose-600">Failed to load: {String(err)}</p>}

      {rows.length > 0 && !colsOk && (
        <p className="text-sm text-amber-800">
          Could not detect columns for ROI. Need a <strong>date</strong> column, stake column (
          <code className="text-xs">$</code> or “stake” / “wager”), and a <strong>result</strong> column. Found:{' '}
          {columns.join(', ') || '(none)'}.
        </p>
      )}

      {rows.length > 0 && colsOk && (
        <div className="card space-y-4 p-4">
          <div className="flex flex-wrap items-end gap-4 text-sm">
            <label className="flex flex-col gap-1">
              <span className="text-slate-500">Year</span>
              <select
                className="rounded border border-slate-300 bg-white px-2 py-1"
                value={filterYear ?? ''}
                onChange={(e) => {
                  const v = e.target.value
                  setFilterYear(v === '' ? null : Number(v))
                }}
              >
                <option value="">All years</option>
                {GOLF_FILTER_YEARS.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            {kpis && (
              <>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500">ROI · {yearLabel}</div>
                  <div
                    className={`text-2xl font-semibold tabular-nums ${
                      kpis.roiPct >= 0 ? 'text-teal-700' : 'text-rose-700'
                    }`}
                  >
                    {kpis.roiPct >= 0 ? '+' : ''}
                    {kpis.roiPct.toFixed(2)}%
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Cumulative profit · {yearLabel}
                  </div>
                  <div
                    className={`text-2xl font-semibold tabular-nums ${
                      kpis.profit >= 0 ? 'text-blue-700' : 'text-rose-700'
                    }`}
                  >
                    {kpis.profit > 0 ? '+' : ''}
                    {kpis.profit.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Total wagered · {yearLabel}
                  </div>
                  <div className="text-2xl font-semibold tabular-nums text-violet-800">
                    {kpis.wagered.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="text-xs text-slate-400">
            Columns: <span className="font-mono text-slate-600">{columnMap.date}</span>,{' '}
            <span className="font-mono text-slate-600">{columnMap.stake}</span>,{' '}
            <span className="font-mono text-slate-600">{columnMap.result}</span>
            {datasetQuery.data?.total != null ? (
              <>
                {' '}
                · {datasetQuery.data.total.toLocaleString()} row{datasetQuery.data.total === 1 ? '' : 's'} for this
                selection
              </>
            ) : null}
            {roiModel.ready && roiModel.skippedRows > 0 ? (
              <> · {roiModel.skippedRows} chart rows skipped (invalid date / stake / result)</>
            ) : null}
          </div>

          {roiModel.series.length === 0 ? (
            <p className="text-sm text-slate-500">No chart points for this selection (check dates and stakes).</p>
          ) : (
            <div className="min-w-0 space-y-2">
              <label className="flex flex-col gap-1">
                <span className="text-sm font-medium text-slate-700">Chart metric</span>
                <select
                  className="w-full max-w-xs rounded border border-slate-300 bg-white px-2 py-1 text-sm"
                  value={chartMetric}
                  onChange={(e) => setChartMetric(e.target.value as ChartMetric)}
                >
                  <option value="roi">Cumulative ROI (%)</option>
                  <option value="profit">Cumulative profit ($)</option>
                  <option value="wagered">Cumulative wagered ($)</option>
                </select>
              </label>
              <div className="h-[22rem] w-full min-w-0">
                <VegaEmbed
                  spec={chartSpec}
                  options={{ actions: false }}
                  className="h-full w-full [&_svg]:max-w-full"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {rows.length > 0 && visibleSheetColumns.length === 0 && (
        <p className="text-sm text-amber-800">
          No columns matched the expected sheet layout (Date, Sport, Site, …). Found:{' '}
          <span className="font-mono text-xs">{columns.join(', ') || '—'}</span>.
        </p>
      )}

      {rows.length > 0 && visibleSheetColumns.length > 0 && (
        <div className="card overflow-hidden p-0">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-2 text-xs text-slate-500">
            <span>
              <span className="font-medium text-slate-600">{yearLabel}</span>
              {' · '}
              {tableTotal.toLocaleString()} matching row{tableTotal === 1 ? '' : 's'}
              {tableTotal > ROI_TABLE_CAP ? ` (showing first ${ROI_TABLE_CAP.toLocaleString()})` : ''}
              {datasetQuery.data?.total != null && datasetQuery.data.total > rows.length
                ? ` — loaded ${rows.length.toLocaleString()} of ${datasetQuery.data.total.toLocaleString()}`
                : rows.length
                  ? ` — ${rows.length.toLocaleString()} loaded`
                  : null}
            </span>
            <button
              type="button"
              className="btn-secondary !py-1 !px-2 text-xs"
              disabled={!filterActive}
              onClick={clearFilters}
            >
              Clear filters
            </button>
          </div>
          <div className="gs-betting-wrap">
            <table className="gs-betting-table">
              <thead>
                <tr>
                  {visibleSheetColumns.map(({ id, label }) => (
                    <SortableTableHeader
                      key={id}
                      label={label}
                      sortKey={id}
                      activeKey={activeSort?.id ?? null}
                      direction={activeSort?.dir ?? 'asc'}
                      onSort={onHeaderClick}
                    />
                  ))}
                </tr>
                <tr>
                  {visibleSheetColumns.map(({ id }) => (
                    <th key={`f-${id}`} scope="col">
                      <input
                        className="gs-filter-input"
                        type="search"
                        autoComplete="off"
                        placeholder="Filter…"
                        aria-label={`Filter ${id}`}
                        value={filters[id] ?? ''}
                        onChange={(e) => setFilters((f) => ({ ...f, [id]: e.target.value }))}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableRows.map((r, i) => (
                  <tr
                    key={`${i}-${visibleSheetColumns.map(({ id }) => `${id}:${fmtCell(r[id])}`).join('|')}`}
                  >
                    {visibleSheetColumns.map(({ id }) => (
                      <td key={id} title={fmtCell(r[id])}>
                        {fmtCell(r[id])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

function fmtCell(v: unknown): string {
  if (v == null) return ''
  if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  return String(v)
}
